import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PRODUCT_TITLES } from '../../services/permissions/constants/products';
import type { Key } from '@adobe/react-spectrum';
import authorizationService from '../../services/AuthorizationService';
import tbwAuthorizationService from '../../services/TbwAuthorizationService';

interface AppState {
    searchTextTB: string;
    searchTextCP: string;
    featureFlags: string[];
    currentApp: string | undefined;
    appsWithRoles: string[];
    loginStatus: string;
    localeUI: string;
    localeUtut: string;
    currentTab: Key;
    /**
     * null  = not yet fetched / request in flight
     * object = result from last fetch (all-false means user has no CP access)
     */
    searchPermissions: {
        canSearchTutorials: boolean;
        canSearchPlaylists: boolean;
        canSearchCollections: boolean;
    } | null;
    /**
     * null  = not yet fetched / request in flight
     * true  = user can view User Management
     * false = user cannot (or fetch failed — secure default)
     */
    canViewUserManagement: boolean | null;
}

const initialState = {
    searchTextTB: '',
    searchTextCP: '',
    featureFlags: [],
    currentApp: undefined,
    appsWithRoles: [],
    loginStatus: '',
    localeUI: 'en_US',
    localeUtut: 'en_US',
    currentTab: PRODUCT_TITLES.TUTORIAL_BUILDER_WEB,
    searchPermissions: null,
    canViewUserManagement: null,
} as AppState;

// ---------------------------------------------------------------------------
// Async thunks: fetch session-scoped permissions once after login
// ---------------------------------------------------------------------------

/**
 * Fetches CP search permissions (SEARCH_TUTORIALS, SEARCH_PLAYLISTS, SEARCH_COLLECTIONS).
 * Skips the call if permissions are already loaded (searchPermissions !== null).
 * Dispatch from HeaderNav when loginStatus === 'success'.
 */
export const fetchSearchPermissions = createAsyncThunk<
    { canSearchTutorials: boolean; canSearchPlaylists: boolean; canSearchCollections: boolean },
    void,
    { state: { app: AppState } }
>(
    'app/fetchSearchPermissions',
    async () => {
        return await authorizationService.getSearchPermissions();
    },
    {
        condition: (_, { getState }) => getState().app.searchPermissions === null,
    },
);

/**
 * Fetches whether the current user can view User Management.
 * Skips the call if already loaded (canViewUserManagement !== null).
 * Dispatch from HeaderNav when loginStatus === 'success'.
 */
export const fetchUserManagementCapability = createAsyncThunk<boolean, void, { state: { app: AppState } }>(
    'app/fetchUserManagementCapability',
    async () => {
        // User Management is allowed if EITHER domain (LCP or TBW) grants LIST_USER_RECORDS.
        // Each service owns its own domain; OR the results.
        const [lcp, tbw] = await Promise.all([
            authorizationService.canViewUserManagementInLCP(),
            tbwAuthorizationService.canViewUserManagementInTBW(),
        ]);
        return lcp || tbw;
    },
    {
        condition: (_, { getState }) => getState().app.canViewUserManagement === null,
    },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const appSlice = createSlice({
    name: 'search',
    initialState,
    reducers: {
        updateTBSearch(state, action: PayloadAction<string>) {
            state.searchTextTB = action.payload;
        },
        updateCPSearch(state, action: PayloadAction<string>) {
            state.searchTextCP = action.payload;
        },
        updateFeatureFlags(state, action: PayloadAction<string>) {
            state.featureFlags = [...state.featureFlags, action.payload];
        },
        updateUserApps(state, action: PayloadAction<string[]>) {
            state.appsWithRoles = action.payload;
            if (state.currentApp === undefined && state.appsWithRoles.length > 0) {
                state.currentApp = state.appsWithRoles[0];
            }
        },
        updateCurrentApp(state, action: PayloadAction<string>) {
            state.currentApp = action.payload;
        },
        updateLoginStatus(state, action: PayloadAction<string>) {
            state.loginStatus = action.payload;
        },
        updateLocaleUI(state, action: PayloadAction<string>) {
            state.localeUI = action.payload;
        },
        updateLocaleUtut(state, action: PayloadAction<string>) {
            state.localeUtut = action.payload;
        },
        updateCurrentTab(state, action: PayloadAction<Key>) {
            state.currentTab = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // fulfilled: store the result — null → resolved object
            .addCase(fetchSearchPermissions.fulfilled, (state, action) => {
                state.searchPermissions = action.payload;
            })
            // rejected: set to all-false so loading stops; null would re-trigger the condition
            .addCase(fetchSearchPermissions.rejected, (state) => {
                state.searchPermissions = {
                    canSearchTutorials: false,
                    canSearchPlaylists: false,
                    canSearchCollections: false,
                };
            })

            .addCase(fetchUserManagementCapability.fulfilled, (state, action) => {
                state.canViewUserManagement = action.payload;
            })
            .addCase(fetchUserManagementCapability.rejected, (state) => {
                state.canViewUserManagement = false;
            });
    },
});

export const {
    updateLocaleUI,
    updateTBSearch,
    updateCPSearch,
    updateFeatureFlags,
    updateUserApps,
    updateCurrentApp,
    updateLoginStatus,
    updateLocaleUtut,
    updateCurrentTab,
} = appSlice.actions;
export default appSlice.reducer;
